import { useState, useEffect } from 'react';
import { Save, Loader2 } from 'lucide-react';
import { insertTable, updateTable } from '@/lib/api';
import type { Settings } from '@/types';
import { ImageUpload } from '@/components/ImageUpload';

interface SettingsManagerProps {
  settings: Settings | null;
  onRefresh: () => void;
}

export function SettingsManager({ settings, onRefresh }: SettingsManagerProps) {
  const [form, setForm] = useState({
    business_name: '',
    whatsapp_number: '',
    logo_url: '',
  });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (settings) {
      setForm({
        business_name: settings.business_name,
        whatsapp_number: settings.whatsapp_number,
        logo_url: settings.logo_url,
      });
    }
  }, [settings]);

  const save = async () => {
    setSaving(true);
    setSaved(false);
    try {
      const payload = {
        business_name: form.business_name,
        whatsapp_number: form.whatsapp_number,
        logo_url: form.logo_url,
      };

      if (settings) {
        await updateTable('settings', { ...payload, id: settings.id });
      } else {
        await insertTable('settings', payload);
      }
      setSaved(true);
      onRefresh();
      setTimeout(() => setSaved(false), 2500);
    } catch {
      alert('Erro ao salvar configurações.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <h2 className="mb-4 font-display text-xl font-bold text-warm-900">Configurações</h2>
      <div className="card space-y-5 p-5">
        <ImageUpload
          value={form.logo_url}
          onChange={(url) => setForm({ ...form, logo_url: url })}
          label="Logo do estabelecimento"
          aspect="square"
          fileNameHint={form.business_name ? `${form.business_name} logo` : 'logo'}
        />

        <div>
          <label className="mb-1.5 block text-sm font-medium text-warm-700">
            Nome do estabelecimento
          </label>
          <input
            className="input-field"
            value={form.business_name}
            onChange={(e) => setForm({ ...form, business_name: e.target.value })}
            placeholder="Ex: Pizzaria Bella"
          />
        </div>

        <div>
          <label className="mb-1.5 block text-sm font-medium text-warm-700">
            Número do WhatsApp (para receber pedidos)
          </label>
          <input
            className="input-field"
            value={form.whatsapp_number}
            onChange={(e) => setForm({ ...form, whatsapp_number: e.target.value })}
            placeholder="Ex: 5511999999999 (DDI + DDD + número)"
          />
          <p className="mt-1.5 text-xs text-warm-500">
            Inclua o código do país (55 para Brasil), DDD e número. Ex: 5511987654321
          </p>
        </div>

        <div className="flex items-center gap-3">
          <button onClick={save} disabled={saving} className="btn-primary">
            {saving ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />}
            Salvar Configurações
          </button>
          {saved && (
            <span className="text-sm font-medium text-green-600 animate-fade-in">
              Configurações salvas!
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
